/**
 * IndexedDB 위의 vault 헤더·기기 정보 관리.
 */
import { uuidv7 } from 'uuidv7';
import { SCHEMA_VERSION } from '@/domain/schema/v1';
import type { DataKey } from './crypto/envelope';
import type { KdfParams } from './crypto/kdf';
import {
  changePassphrase,
  createVault,
  isVaultHeader,
  recoverWithKey,
  unlockWithPassphrase,
  type VaultHeader,
} from './crypto/vault';
import { META, type CounselDb } from './db/schema';

export async function readVaultHeader(db: CounselDb): Promise<VaultHeader | null> {
  const row = await db.meta.get(META.vault);
  return row && isVaultHeader(row.value) ? row.value : null;
}

export async function getOrCreateDeviceId(db: CounselDb): Promise<string> {
  return db.transaction('rw', db.meta, async () => {
    const row = await db.meta.get(META.deviceId);
    if (typeof row?.value === 'string') return row.value;
    const id = `dev-${uuidv7()}`;
    await db.meta.put({ key: META.deviceId, value: id });
    return id;
  });
}

export async function getMeta<T>(db: CounselDb, key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return row ? (row.value as T) : fallback;
}

export async function setMeta(db: CounselDb, key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export async function initializeVault(
  db: CounselDb,
  passphrase: string,
  kdf: KdfParams,
  opts: { allowWeakKdfForTests?: boolean } = {},
): Promise<{ header: VaultHeader; dataKey: DataKey; recoveryKeyDisplay: string }> {
  const created = await createVault(passphrase, kdf, opts);
  await db.transaction('rw', db.meta, async () => {
    if (await db.meta.get(META.vault)) throw new Error('이미 설정된 vault가 있습니다');
    await db.meta.put({ key: META.vault, value: created.header });
    await db.meta.put({ key: META.dataSchemaVersion, value: SCHEMA_VERSION });
  });
  return created;
}

export async function unlockVault(
  db: CounselDb,
  passphrase: string,
): Promise<{ header: VaultHeader; dataKey: DataKey }> {
  const header = await readVaultHeader(db);
  if (!header) throw new Error('설정된 vault가 없습니다');
  return { header, dataKey: await unlockWithPassphrase(header, passphrase) };
}

export async function changeVaultPassphrase(
  db: CounselDb,
  oldPassphrase: string,
  newPassphrase: string,
  opts: { allowWeakKdfForTests?: boolean } = {},
): Promise<void> {
  const header = await readVaultHeader(db);
  if (!header) throw new Error('설정된 vault가 없습니다');
  const next = await changePassphrase(header, oldPassphrase, newPassphrase, header.kdf, opts);
  await db.meta.put({ key: META.vault, value: next });
}

export async function recoverVault(
  db: CounselDb,
  recoveryKey: string,
  newPassphrase: string,
  opts: { allowWeakKdfForTests?: boolean } = {},
): Promise<{ header: VaultHeader; dataKey: DataKey }> {
  const header = await readVaultHeader(db);
  if (!header) throw new Error('설정된 vault가 없습니다');
  const res = await recoverWithKey(header, recoveryKey, newPassphrase, opts);
  await db.meta.put({ key: META.vault, value: res.header });
  return res;
}
